// src/types/assistant/charts.ts
// Chart DSL Types (v3) and Helpers

// ─────────────────────────────────────────────────────────────────────────────
// Chart Types
// ─────────────────────────────────────────────────────────────────────────────

export type ChartType =
  | "time_series"     // Line/area chart over time
  | "comparison"      // Multi-asset time series indexed to 100
  | "bar"             // Vertical/horizontal bar chart
  | "distribution"    // Sorted bar chart (same as bar, implies sorted)
  | "scatter"         // X-Y scatter/bubble plot
  | "treemap"         // Proportional rectangles
  | "stat_card"       // Single KPI card
  | "heatmap"         // Reserved (not currently generated)
  // Legacy types (mapped internally)
  | "historical"
  | "multi_historical";

export type TimeSeriesStyle = "line" | "area" | "bar";
export type BarOrientation = "horizontal" | "vertical";
export type StackingMode = "none" | "stacked" | "100_percent";
export type ValueFormat = "currency" | "percent" | "ratio" | "number" | "multiplier" | "compact";

// ─────────────────────────────────────────────────────────────────────────────
// Axis Configuration
// ─────────────────────────────────────────────────────────────────────────────

export interface AxisConfig {
  label: string;
  unit?: string;                           // "$", "%", "B", etc.
  type?: "time" | "category" | "numeric";
  format?: ValueFormat;
  log_scale?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat Card Data
// ─────────────────────────────────────────────────────────────────────────────

export interface StatCardData {
  label: string;
  value: string | number;
  change?: number;
  change_period?: string;
  format: ValueFormat;
  trend?: "up" | "down" | "neutral";
  subtitle?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Treemap Node
// ─────────────────────────────────────────────────────────────────────────────

export interface TreemapNode {
  name: string;
  value: number;
  color_value?: number;
  category?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// MetricSpec - Structured metric specification with optional transforms
// ─────────────────────────────────────────────────────────────────────────────

export type MetricTransform = "change" | "rollup";
export type TimeWindow = "1d" | "7d" | "30d" | "60d" | "90d" | "180d" | "365d";

/**
 * MetricSpec defines how to fetch a metric value.
 * 
 * Three types:
 * 1. Point metrics (no transform): { key: "price_close" } → /tokens/current
 * 2. Change metrics: { key: "price_close", transform: "change", window: "1d" } → /tokens/changes
 * 3. Rollup metrics: { key: "volume", transform: "rollup", window: "7d" } → /tokens/rollups
 * 
 * Note: Derived metrics like "sharpe_90d" or "vol_30d" have the window baked into the key,
 * so they use point metric fetching even though they represent windowed calculations.
 */
export interface MetricSpec {
  key: string;                             // Exact metric key (required)
  transform?: MetricTransform | null;      // Optional transform type
  window?: TimeWindow | null;              // Required when transform is set
}

/** Metric can be a string (legacy) or MetricSpec object */
export type ChartMetric = MetricSpec | string;

// ─────────────────────────────────────────────────────────────────────────────
// ChartSpec (DSL v3)
// ─────────────────────────────────────────────────────────────────────────────

export interface ChartSpec {
  // Required fields
  id: string;                              // "chart_1", "chart_2", etc.
  type: ChartType;
  title: string;
  
  // Optional description
  description?: string;
  
  // Primary metric (for most chart types) - accepts string or MetricSpec
  metric?: ChartMetric;
  metric_label?: string;                   // Human-readable label
  
  // Data scope
  tickers?: string[];                      // Token symbols: ["ETH", "SOL"]
  ticker?: string;                         // Single ticker (for stat_card)
  categories?: string[];                   // Sector names: ["L1", "DeFi"]
  category?: string;                       // Single category (for stat_card)
  
  // Time context
  timeframe?: string;                      // "24h", "7d", "30d", "90d", "1y", "2y"
  change_window?: TimeWindow;              // Change window for stat_card
  
  // Axis config
  x_axis?: AxisConfig;
  y_axis?: AxisConfig;
  
  // For scatter/treemap plots - accepts string or MetricSpec
  x_metric?: ChartMetric;
  y_metric?: ChartMetric;
  size_metric?: ChartMetric;               // Bubble size / treemap area
  color_metric?: ChartMetric;              // Color encoding
  
  // Stat card specific
  stats?: StatCardData[];                  // Backend may send stat_card data here
  stat_data?: StatCardData;                // For stat_card
  
  // Treemap specific
  nodes?: TreemapNode[];                   // Pre-computed treemap data
  group_by_category?: boolean;             // Group rectangles by category
  
  // Display options - time series
  style?: TimeSeriesStyle;                 // "line" | "area" | "bar"
  stacked?: boolean;                       // For area/bar stacking
  stacking?: StackingMode;                 // "none" | "stacked" | "100_percent"
  index_to_100?: boolean;                  // For comparison charts
  benchmarks?: string[];                   // Benchmark tickers to highlight
  log_scale?: boolean;                     // Logarithmic y-axis
  
  // Display options - bar
  orientation?: BarOrientation;            // "horizontal" | "vertical"
  sorted?: boolean;                        // Sort by value
  color_by_value?: boolean;                // Green for positive, red for negative
  
  // Display options - scatter
  show_quadrants?: boolean;                // Show quadrant dividers at median
  show_regression?: boolean;               // Show trend line
  log_x?: boolean;
  log_y?: boolean;
  
  // General display options
  show_legend?: boolean;
  show_labels?: boolean;
  
  // Optional: pre-computed data if available
  data_hint?: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// MetricSpec Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize a ChartMetric to MetricSpec format.
 */
export function normalizeMetricSpec(metric: ChartMetric): MetricSpec {
  if (typeof metric === "string") {
    return { key: metric };
  }
  return metric;
}

/**
 * Check if metric spec requires windowed data fetching
 */
export function isWindowedMetric(spec: MetricSpec): boolean {
  return spec.transform === "change" || spec.transform === "rollup";
}

/**
 * Get normalized MetricSpec from ChartSpec
 */
export function getMetricSpec(spec: ChartSpec): MetricSpec | undefined {
  if (!spec.metric) return undefined;
  return normalizeMetricSpec(spec.metric);
}

/**
 * Get size_metric MetricSpec from ChartSpec
 */
export function getSizeMetricSpec(spec: ChartSpec): MetricSpec | undefined {
  if (!spec.size_metric) return undefined;
  return normalizeMetricSpec(spec.size_metric);
}

/**
 * Get color_metric MetricSpec from ChartSpec
 */
export function getColorMetricSpec(spec: ChartSpec): MetricSpec | undefined {
  if (!spec.color_metric) return undefined;
  return normalizeMetricSpec(spec.color_metric);
}

/**
 * Get normalized x_metric MetricSpec from ChartSpec
 */
export function getXMetricSpec(spec: ChartSpec): MetricSpec | undefined {
  if (!spec.x_metric) return undefined;
  return normalizeMetricSpec(spec.x_metric);
}

/**
 * Get normalized y_metric MetricSpec from ChartSpec
 */
export function getYMetricSpec(spec: ChartSpec): MetricSpec | undefined {
  if (!spec.y_metric) return undefined;
  return normalizeMetricSpec(spec.y_metric);
}

// ─────────────────────────────────────────────────────────────────────────────
// Value Formatting Helper
// ─────────────────────────────────────────────────────────────────────────────

export function formatStatValue(value: number | string, format: ValueFormat): string {
  if (typeof value === "string") return value;
  
  switch (format) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: Math.abs(value) >= 1e9 ? "compact" : "standard",
        maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2,
      }).format(value);
    
    case "percent":
      return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
    
    case "ratio":
      return value.toFixed(2);
    
    case "number":
      return new Intl.NumberFormat("en-US").format(value);
    
    case "multiplier":
      return `${value.toFixed(1)}x`;
    
    case "compact":
      return new Intl.NumberFormat("en-US", { notation: "compact" }).format(value);
    
    default:
      return String(value);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Chart Placeholder in Markdown
// ─────────────────────────────────────────────────────────────────────────────

export interface ChartPlaceholder {
  type: "chart";
  chartId: string;
  spec: ChartSpec | null;
}

export type ContentBlock = string | ChartPlaceholder;

/**
 * Parse markdown with chart placeholders
 */
export function parseReportWithCharts(
  markdown: string,
  chartMap: Map<string, ChartSpec>
): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  const pattern = /\[\[chart:([^\]]+)\]\]/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(markdown)) !== null) {
    // Text before placeholder
    if (match.index > lastIndex) {
      blocks.push(markdown.slice(lastIndex, match.index));
    }

    // Chart placeholder
    const chartId = match[1]!;
    blocks.push({
      type: "chart",
      chartId,
      spec: chartMap.get(chartId) ?? null,
    });

    lastIndex = pattern.lastIndex;
  }

  // Remaining text
  if (lastIndex < markdown.length) {
    blocks.push(markdown.slice(lastIndex));
  }

  return blocks;
}

